import styled, { Navbar } from "grabcad-ui-elements";
import { useContext } from "react";
import { GCApplication } from "grabcad-ui-elements/dist/ui/General/ApplicationLogo";
import { ApplicationContext } from "../components/ApplicationProvider";
import openGcpAsync from "../view/Navigation/openGcpAsync";
import { UserContext } from "../components/User/UserProvider";
import { PlatformConstants } from "grabcad-ui-elements/dist/Theme";
import { useHistory } from "react-router-dom";
import isSdkPage from "../utils/isSdkPage";
import { config } from "../config";
import { fullAnalyzeUrl, PATHS } from "../Routes";
import { Notifier } from "../view/Notifier";
import { useCompanyAsAdminQuery } from "../graphql";
import { useCompanyLicensesQuery } from "../graphql/Queries/Licenses/GetCompanyLicenses";
import { AnalyzeDeploymentType } from "./ConfigureAnalyze/AnalyzeSelection";
import { useMutation } from "@apollo/client";
import { CREATE_EVENT } from "../graphql/Mutations/Event";
import {
    EventCategory,
    METRIC_EVENT_CATALOG,
    MetricEventKey,
} from "@grabcad/company-server-shared/dist/types";

export const NAVBAR_HEIGHT_PX = `${PlatformConstants.NAVBAR_HEIGHT_PX}px`;

export const TopNavbarWrapper = ({
    logout,
    updatePassword,
    openShop,
}: ITopNavbarWrapperProps): JSX.Element => {
    const { t } = useContext(ApplicationContext);
    const { name } = useContext(UserContext);
    const history = useHistory();
    const { company } = useCompanyAsAdminQuery();
    const [triggerCreateEvent] = useMutation(CREATE_EVENT);
    const {
        enabledFeatures: { sdkAccess, analyzeAccess },
        allEnabledFeatureKeys,
    } = useCompanyLicensesQuery();

    const isSdk = isSdkPage();

    let data = {
        userDropdown: {
            pointing: true,
            fullName: name,
            options: [
                {
                    key: 1,
                    text: t("top_navbar_update_password"),
                    value: 1,
                    active: false,
                    selected: false,
                    onClick: updatePassword,
                    icon: "lock",
                },
                {
                    key: 2,
                    text: t("top_navbar_user_settings"),
                    value: 2,
                    active: false,
                    selected: false,
                    onClick: () => (window.location.href = config.REACT_APP_USER_SETTINGS_URL!),
                    icon: "user circle outline",
                },
                {
                    key: 3,
                    text: t("top_navbar_logout"),
                    value: 3,
                    active: false,
                    selected: false,
                    onClick: logout,
                    icon: "sign out alternate",
                },
            ],
        },
        mainDropdown: {
            pointing: true,
            text: t("top_navbar_contact_us"),
            "data-testid": "mainDropdown",
            options: [
                {
                    key: 1,
                    text: t("top_navbar_contact_us"),
                    value: 1,
                    selected: false,
                    active: false,
                    "data-testid": "mainDropdown-contactUs",
                    onClick: () =>
                        window.open("https://support.stratasys.com/contact-us", "_blank"),
                    icon: "mail",
                },
                {
                    key: 2,
                    text: t("top_navbar_request_demo"),
                    value: 2,
                    selected: false,
                    active: false,
                    "data-testid": "mainDropdown-requestDemo",
                    onClick: () =>
                        window.location.assign(
                            "https://calendly.com/d/yk5-vqs-grj/grabcad-control-shop-demo"
                        ),
                    icon: "video",
                },
            ],
        },
        helpDropdown: {
            pointing: true,
            options: [
                {
                    key: 1,
                    value: 1,
                    text: t("feedback.help_link"),
                    selected: false,
                    active: false,
                    icon: "question",
                    onClick: () =>
                        window.location.assign(
                            "https://help.grabcad.com/category/261-about-grabcad-shop"
                        ),
                },
                {
                    key: 2,
                    value: 2,
                    selected: false,
                    active: false,
                    children: (
                        <>
                            <i aria-hidden="true" className="list icon"></i>
                            <a
                                href={`${process.env.PUBLIC_URL}/acknowledgements.txt`}
                                download={"acknowledgements"}
                            >
                                {t("feedback.acknowledgements")}
                            </a>
                        </>
                    ),
                },
            ],
        },
        applications: [
            {
                application: "GC_CONTROL" as GCApplication,
                active: !isSdk,
                name: "Control",
                onClick: () => {
                    if (isSdk) {
                        history.push("/");
                    }
                },
            },
            {
                application: "GC_SHOP" as GCApplication,
                name: "Shop",
                onClick: openShop,
            },
            {
                application: "GC_PRINT" as GCApplication,
                name: "Print",
                onClick: async () => openGcpAsync(t),
            },
            {
                application: "GC_DEVELOP" as GCApplication,
                name: "Develop",
                active: isSdk,
                onClick: () => {
                    if (!isSdk) {
                        if (sdkAccess) {
                            history.push("/sdk");
                            return;
                        }
                        window.open("https://grabcad.com/software-development-kit", "_blank");
                    }
                },
            },
        ],
        featureKeys: allEnabledFeatureKeys,
    };

    if (analyzeAccess) {
        data.applications.push({
            application: "GC_ANALYZE" as GCApplication,
            name: "Analyze",
            onClick: () => {
                const { analyzeDeploymentType, analyzeServerAddress } = company || {};
                const isOnPrem = analyzeDeploymentType === AnalyzeDeploymentType.onPrem;
                const isSaas = analyzeDeploymentType === AnalyzeDeploymentType.saas;
                const hasServerAddress = Boolean(analyzeServerAddress);

                // Log event (non-blocking)
                if (!company?.internal) {
                    const { action, description } =
                        METRIC_EVENT_CATALOG[MetricEventKey.LAUNCH_ANALYZE_FROM_TOPNAV];

                    triggerCreateEvent({
                        variables: {
                            events: [
                                {
                                    event: EventCategory.GC_ANALYZE,
                                    action,
                                    description,
                                },
                            ],
                        },
                    }).catch((err) => console.error("Failed to log event:", err));
                }

                if (
                    (isOnPrem && hasServerAddress) ||
                    ((!analyzeDeploymentType ||
                        analyzeDeploymentType !== AnalyzeDeploymentType.saas) &&
                        hasServerAddress)
                ) {
                    window.location.href = fullAnalyzeUrl(analyzeServerAddress!, "onPrem");
                } else if (
                    isSaas &&
                    config.ANALYZE_SAAS_URL &&
                    allEnabledFeatureKeys.has("analyze_saas_user")
                ) {
                    window.location.href = fullAnalyzeUrl(config.ANALYZE_SAAS_URL);
                } else if (allEnabledFeatureKeys.has("analyze_saas_user")) {
                    history.push(PATHS.configureAnalyze);
                } else {
                    Notifier.warn(t("launch_analyze.warn_no_url"));
                }
            },
        });
    }

    return (
        <StyledTopNavbarWrapper className="top-navbar-wrapper">
            <Navbar {...data} />
        </StyledTopNavbarWrapper>
    );
};

interface ITopNavbarWrapperProps {
    logout: () => void;
    updatePassword: () => void;
    openShop: () => void;
}

const StyledTopNavbarWrapper = styled.div`
    flex: 0 0 auto;
`;
